// Roles API — wired to real backend.
//
// Backend paths:
//   GET    /api/v1/admin/roles                 (?includeSystem)
//   GET    /api/v1/admin/roles/{id}
//   GET    /api/v1/admin/roles/{id}/permissions   // ISSUE-51 — top-level Items list
//   POST   /api/v1/admin/roles                 { name, description, permissions[] }
//   PUT    /api/v1/admin/roles/{id}            { name, description }   // ISSUE-50 — rename
//   PUT    /api/v1/admin/roles/{id}/permissions { permissions[] }
//   DELETE /api/v1/admin/roles/{id}            (soft delete, fails for system roles)

import { http, unwrap, unwrapList, type ActionResponse } from './http';

export interface RoleDto {
  id: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  permissions: string[];
  userCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RoleListQuery {
  includeSystem?: boolean;
}

export interface CreateRoleInput {
  name: string;
  description?: string;
  permissions?: string[];
}

export interface UpdateRoleInput {
  name: string;
  description?: string | null;
}

export interface UpdateRolePermissionsInput {
  permissions: string[];
}

interface RawRoleDto {
  id: string;
  name: string | null;
  description: string | null;
  isSystem: boolean;
  permissions: string[] | null;
  userCount: number;
  createdAt: string;
  updatedAt: string;
}

const BASE = '/api/v1/admin/roles';

function mapRole(dto: RawRoleDto): RoleDto {
  return {
    id: dto.id,
    name: dto.name ?? '',
    description: dto.description,
    isSystem: dto.isSystem,
    permissions: dto.permissions ?? [],
    userCount: dto.userCount,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

export const rolesApi = {
  async list(query: RoleListQuery = {}): Promise<RoleDto[]> {
    const params: Record<string, string | number | boolean> = {};
    if (query.includeSystem !== undefined) {
      params.includeSystem = query.includeSystem;
    }
    const res = await http.get<ActionResponse<RawRoleDto>>(BASE, { params });
    const { items } = unwrapList(res);
    return items.map(mapRole);
  },

  async get(id: string): Promise<RoleDto> {
    const dto = unwrap(await http.get<ActionResponse<RawRoleDto>>(`${BASE}/${id}`));
    return mapRole(dto);
  },

  /**
   * ISSUE-51 — Fetch only the permission strings for a role. Returned as a
   * top-level `items` list so the FE doesn't have to unwrap `RoleDto.permissions`.
   */
  async getPermissions(id: string): Promise<string[]> {
    const res = await http.get<ActionResponse<string>>(`${BASE}/${id}/permissions`);
    const { items } = unwrapList(res);
    return items;
  },

  /**
   * Canonical permission catalog — every policy string the backend enforces.
   * Single source of truth for the role editor's permission universe (no longer
   * inferred from the union of existing roles).
   */
  async catalog(): Promise<string[]> {
    const res = await http.get<ActionResponse<string>>('/api/v1/admin/permissions');
    const { items } = unwrapList(res);
    return items;
  },

  async create(input: CreateRoleInput): Promise<string> {
    const newId = unwrap(
      await http.post<ActionResponse<string>>(BASE, {
        name: input.name,
        description: input.description ?? null,
        permissions: input.permissions ?? [],
      }),
    );
    return newId;
  },

  /**
   * ISSUE-50 — Rename a role (and/or update description). System roles are
   * rejected by the BE with `SYSTEM_ROLE_IMMUTABLE` (HTTP 422). The full
   * refreshed RoleDto is returned.
   */
  async update(id: string, input: UpdateRoleInput): Promise<RoleDto> {
    const dto = unwrap(
      await http.put<ActionResponse<RawRoleDto>>(`${BASE}/${id}`, {
        name: input.name,
        description: input.description ?? null,
      }),
    );
    return mapRole(dto);
  },

  async updatePermissions(
    id: string,
    input: UpdateRolePermissionsInput,
  ): Promise<void> {
    await http.put<ActionResponse<boolean>>(`${BASE}/${id}/permissions`, {
      permissions: input.permissions,
    });
  },

  async remove(id: string): Promise<void> {
    await http.delete<ActionResponse<boolean>>(`${BASE}/${id}`);
  },
};

export type RolesApi = typeof rolesApi;
